import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { zslAtom } from "../templates";
import { TableField } from "../types";
import {
  extractAssetId,
  extractErrorMessageFromResponse,
  isTemplateTableId,
} from "../utils";
import { getSubpathBridgeClientFetch } from "@zapier/identity/client/fetch";
import * as z from "zod";
import { getTableResponseSchema } from "./schemas/tablesApiSchemas";

const tablesClient = getSubpathBridgeClientFetch("tables");

export type TableFetchResult =
  | {
      type: "success";
      data: z.infer<typeof getTableResponseSchema>["data"];
    }
  | {
      type: "not_found";
    }
  | {
      type: "forbidden";
    };
type ZSLTable = { name: string; id: string; fields: TableField[] };

export type ZSLTableResult = {
  type: "success";
  data: ZSLTable;
};

/**
 * Fetches table via the bridge client to Tables API (`/api/v1/tables/:id`).
 * This endpoint will return 200 when the user has access to the table.
 * If the user does not have access to the table, the endpoint will return 403.
 * If the table is not found, the endpoint will return 404.
 * If there is an error (5xx or other unexpected errors), the endpoint will return null.
 */
export const tableAtomFamily_SUSPENDS = atomFamily(
  (tableId: string | undefined) => {
    const tableAtom = atom(
      async (get): Promise<TableFetchResult | ZSLTableResult | null> => {
        if (!tableId) {
          return null;
        }

        const isTemplateTable = isTemplateTableId(tableId);
        const zsl = get(zslAtom);

        if (zsl && isTemplateTable) {
          const coreTableId = extractAssetId(tableId);

          const tableTemplate = zsl.tables?.find(
            (table) => coreTableId === table.id,
          );

          if (tableTemplate) {
            return {
              type: "success",
              data: tableTemplate,
            };
          }
        }

        // Because we don't want to fetch with template asset id and get a 404 Not Found error
        if (isTemplateTable) {
          return null;
        }

        try {
          const res: unknown = await tablesClient(`/v1/tables/${tableId}`);

          if (!(res instanceof Response)) {
            console.error(
              `Unknown response type from bridge client for table ${tableId}`,
            );
            return null;
          }

          if (res.ok) {
            const json: unknown = await res.json();
            return {
              type: "success",
              data: getTableResponseSchema.parse(json).data,
            };
          } else {
            if (res.status === 404) {
              return {
                type: "not_found",
              };
            } else if (res.status === 403) {
              return {
                type: "forbidden",
              };
            } else {
              /* ----- 5xx or other unexpected errors – log & degrade ----- */
              const errorMessage = await extractErrorMessageFromResponse(res);
              console.error(
                `Failed to fetch table ${tableId}: ${errorMessage}`,
              );
              return null;
            }
          }
        } catch (error) {
          if (error instanceof z.ZodError) {
            const prettyError = z.prettifyError(error);
            console.error(
              `Table API response validation failed for table ${tableId}:`,
              prettyError,
            );
          } else {
            console.error(`Error fetching table ${tableId}:`, error);
          }

          return null;
        }
      },
    );

    tableAtom.debugLabel = `tableAtomFamily_SUSPENDS(${tableId})`;
    return tableAtom;
  },
);
